import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Plus, ArrowLeft, BookOpen, MoreVertical, Edit, Trash2, PenLine, Play, GripVertical } from 'lucide-react';
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Toast from '../../components/Toast';
import {
    getDecks,
    createDeck,
    updateDeck,
    deleteDeck,
    reorderDecks,
} from '../services/userContent.service';
import { UserDeck } from '../types/userContent.types';
import './UserDeckPage.css';

// ── Paleta de cores ──────────────────────────────────────────────────────────
const DECK_COLORS: { id: string; hex: string; label: string }[] = [
    { id: 'blue-deep',    hex: '#042563', label: 'Azul Profundo' },
    { id: 'blue-vibrant', hex: '#003BD1', label: 'Azul Vibrante' },
    { id: 'blue-royal',   hex: '#1E40AF', label: 'Azul Royal' },
    { id: 'blue-medium',  hex: '#2563EB', label: 'Azul Médio' },
    { id: 'blue-sky',     hex: '#3B82F6', label: 'Azul Claro' },
    { id: 'teal',         hex: '#0F766E', label: 'Teal' },
    { id: 'teal-bright',  hex: '#1D9E75', label: 'Verde-azulado' },
    { id: 'indigo',       hex: '#4F46E5', label: 'Índigo' },
    { id: 'green-deep',   hex: '#14532D', label: 'Verde Profundo' },
    { id: 'green-forest', hex: '#166534', label: 'Verde Floresta' },
    { id: 'green-medium', hex: '#15803D', label: 'Verde Médio' },
    { id: 'purple-deep',  hex: '#4C1D95', label: 'Roxo Profundo' },
    { id: 'slate',        hex: '#334155', label: 'Ardósia' },
    { id: 'amber',        hex: '#B45309', label: 'Âmbar' },
    { id: 'beige',        hex: '#78716C', label: 'Bege' },
];

function getDeckColor(colorId?: string): string {
    return DECK_COLORS.find(c => c.id === colorId)?.hex ?? '#042563';
}

// ── Color Picker ─────────────────────────────────────────────────────────────
const ColorPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
    <div className="ud-color-row">
        {DECK_COLORS.map(c => (
            <button
                key={c.id}
                type="button"
                className={`ud-color-dot${value === c.id ? ' selected' : ''}`}
                style={{ background: c.hex }}
                onClick={() => onChange(c.id)}
                title={c.label}
            />
        ))}
    </div>
);

// ── Card Menu ─────────────────────────────────────────────────────────────────

interface CardMenuProps {
    onEdit: (e: React.MouseEvent) => void;
    onDelete: () => void;
}
const CardMenu: React.FC<CardMenuProps> = ({ onEdit, onDelete }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="ud-menu-wrap" ref={ref}>
            <button
                className="ud-menu-btn"
                onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
                title="Opções"
            >
                <MoreVertical size={16} />
            </button>
            {open && (
                <div className="ud-menu-dropdown" onClick={e => e.stopPropagation()}>
                    <button onClick={e => { onEdit(e); setOpen(false); }}>
                        <Edit size={14} /> Editar
                    </button>
                    <button className="danger" onClick={() => { onDelete(); setOpen(false); }}>
                        <Trash2 size={14} /> Excluir
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Mastery Bar ───────────────────────────────────────────────────────────────

const MasteryBar: React.FC<{ pct: number | null; cardCount: number; color: string }> = ({ pct, cardCount, color }) => (
    <div className="ud-mastery-wrap">
        <div className="ud-mastery-label-row">
            <span className="ud-card-count">{cardCount} card{cardCount !== 1 ? 's' : ''}</span>
            {pct !== null
                ? <span className="ud-mastery-pct" style={{ color }}>{pct}%</span>
                : <span className="ud-mastery-placeholder">Sem estudo ainda</span>
            }
        </div>
        <div className="ud-mastery-bar-bg">
            <div className="ud-mastery-bar-fill" style={{ width: pct !== null ? `${pct}%` : '0%', background: color }} />
        </div>
    </div>
);

// ── Sortable Deck Card ────────────────────────────────────────────────────────

interface SortableDeckCardProps {
    deck: UserDeck;
    masteryPct: number | null;
    onEdit: (deck: UserDeck, e: React.MouseEvent) => void;
    onDelete: (deck: UserDeck) => void;
    onGoToEditor: (deck: UserDeck) => void;
    onGoToStudy: (deck: UserDeck) => void;
}

const SortableDeckCard: React.FC<SortableDeckCardProps> = ({
    deck, masteryPct, onEdit, onDelete, onGoToEditor, onGoToStudy,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: deck._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
    };

    const deckColor = getDeckColor(deck.colorId);

    return (
        <div ref={setNodeRef} style={{ ...style, '--ud-color': deckColor } as React.CSSProperties} className="ud-card">
            <div className="ud-card-top-bar" style={{ background: deckColor }} />
            <div className="ud-card-body">
                <div className="ud-card-header-row">
                    <div className="ud-card-icon">
                        {deck.iconUrl
                            ? <img src={deck.iconUrl} alt={deck.name} />
                            : <BookOpen size={22} />
                        }
                    </div>
                    <p className="ud-card-name">{deck.name}</p>
                    <button
                        className="ud-drag-handle"
                        title="Reordenar"
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical size={15} />
                    </button>
                    <CardMenu
                        onEdit={e => onEdit(deck, e)}
                        onDelete={() => onDelete(deck)}
                    />
                </div>

                {deck.description && (
                    <p className="ud-card-desc">{deck.description}</p>
                )}

                <MasteryBar pct={masteryPct} cardCount={deck.cardCount ?? 0} color={deckColor} />

                <div className="ud-card-actions">
                    <button
                        className="ud-btn-action"
                        onClick={() => onGoToEditor(deck)}
                    >
                        <PenLine size={13} /> Editar cards
                    </button>
                    <button
                        className="ud-btn-action primary"
                        style={{ background: deckColor, borderColor: deckColor }}
                        onClick={() => onGoToStudy(deck)}
                    >
                        <Play size={13} /> Estudar
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

const SkeletonGrid: React.FC = () => (
    <div className="ud-grid">
        {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ud-skeleton-card">
                <div className="ud-skeleton-line wide" />
                <div className="ud-skeleton-line narrow" />
            </div>
        ))}
    </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

interface FormState { name: string; description: string; colorId: string; }
const EMPTY_FORM: FormState = { name: '', description: '', colorId: 'blue-deep' };

const UserDeckPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { collectionId } = useParams<{ collectionId: string }>();

    const navState = location.state as { collectionName?: string } | null;

    const [decks, setDecks] = useState<UserDeck[]>([]);
    const [collectionName] = useState(navState?.collectionName ?? '');
    const [loading, setLoading] = useState(true);
    const [deckStats, setDeckStats] = useState<Map<string, number>>(new Map());
    const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
    const [editTarget, setEditTarget] = useState<UserDeck | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [deleteTarget, setDeleteTarget] = useState<UserDeck | null>(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        if (collectionId) fetchDecks();
    }, [collectionId]); // eslint-disable-line react-hooks/exhaustive-deps

    async function fetchDecks() {
        setLoading(true);
        try {
            const data = await getDecks(collectionId!);
            setDecks(data);
            fetchStatsForDecks(data);
        } catch {
            showToast('Erro ao carregar decks', 'error');
        } finally {
            setLoading(false);
        }
    }

    const fetchStatsForDecks = useCallback(async (decksToLoad: UserDeck[]) => {
        if (decksToLoad.length === 0) return;
        const token = localStorage.getItem('accessToken');
        const results = await Promise.all(
            decksToLoad.map(async d => {
                try {
                    const r = await fetch(`/api/user-content/study/${d._id}/stats`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!r.ok) return null;
                    const data = await r.json();
                    return { id: d._id as string, mastery: data.masteryPercentage as number };
                } catch {
                    return null;
                }
            })
        );
        const map = new Map<string, number>();
        results.forEach(r => { if (r) map.set(r.id, r.mastery); });
        setDeckStats(map);
    }, []);

    function showToast(message: string, type: 'success' | 'error' | 'info') {
        setToast({ message, type });
    }

    function openCreate() {
        setForm(EMPTY_FORM);
        setEditTarget(null);
        setModalMode('create');
    }

    function openEdit(deck: UserDeck, e: React.MouseEvent) {
        e.stopPropagation();
        setForm({ name: deck.name, description: deck.description, colorId: deck.colorId ?? 'blue-deep' });
        setEditTarget(deck);
        setModalMode('edit');
    }

    function closeModal() {
        setModalMode(null);
        setEditTarget(null);
    }

    async function handleSave() {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            if (modalMode === 'create') {
                const created = await createDeck(collectionId!, form);
                setDecks(prev => [...prev, { ...created, colorId: form.colorId }]);
                showToast('Deck criado', 'success');
            } else if (editTarget) {
                const updated = await updateDeck(editTarget._id, form);
                setDecks(prev => prev.map(d => d._id === updated._id ? { ...d, ...updated, colorId: form.colorId } : d));
                showToast('Deck atualizado', 'success');
            }
            closeModal();
        } catch (err: any) {
            showToast(err.message || 'Erro ao salvar', 'error');
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            await deleteDeck(deleteTarget._id);
            setDecks(prev => prev.filter(d => d._id !== deleteTarget._id));
            showToast('Deck excluído', 'success');
            setDeleteTarget(null);
        } catch (err: any) {
            showToast(err.message || 'Erro ao excluir', 'error');
        } finally {
            setSaving(false);
        }
    }

    function goToEditor(deck: UserDeck) {
        sessionStorage.setItem('userEditorContext', JSON.stringify({
            collectionId,
            deckId: deck._id,
        }));
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate(`/my-flashcards/editor/${deck._id}`);
    }

    function goToStudy(deck: UserDeck) {
        sessionStorage.setItem('userEditorContext', JSON.stringify({
            collectionId,
            deckId: deck._id,
        }));
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate(`/study/user/${deck._id}`);
    }

    function goBack() {
        navigate('/dashboard', { state: { activePage: 'explore', activeExploreTab: 'create-flashcards' } });
    }

    async function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setDecks(prev => {
            const oldIdx = prev.findIndex(d => d._id === active.id);
            const newIdx = prev.findIndex(d => d._id === over.id);
            const reordered = arrayMove(prev, oldIdx, newIdx);
            const items = reordered.map((d, i) => ({ _id: d._id, order: i }));
            reorderDecks(items).catch(() => { });
            return reordered;
        });
    }

    return (
        <div className="ud-page">
            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
            )}

            {/* ── Institutional Header ── */}
            <header className="ud-inst-header">
                <div className="ud-inst-header-inner">
                    <button
                        className="ud-nav-back"
                        onClick={goBack}
                        title="Voltar para Minhas Coleções"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <h1 className="ud-header-title">{collectionName || 'Minha Coleção'}</h1>
                    <button className="ud-btn-new" onClick={openCreate}>
                        <Plus size={15} /> Novo Deck
                    </button>
                </div>
            </header>

            {/* ── Content ── */}
            <div className="ud-content">
                {loading ? (
                    <SkeletonGrid />
                ) : decks.length === 0 ? (
                    <div className="ud-empty">
                        <BookOpen size={56} className="ud-empty-icon" />
                        <p className="ud-empty-title">Nenhum deck ainda</p>
                        <p className="ud-empty-sub">
                            Crie seu primeiro deck para começar a adicionar flashcards
                        </p>
                        <button className="ud-btn-empty-cta" onClick={openCreate}>
                            <Plus size={16} /> Criar primeiro deck
                        </button>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={decks.map(d => d._id)} strategy={rectSortingStrategy}>
                            <div className="ud-grid">
                                {decks.map(deck => (
                                    <SortableDeckCard
                                        key={deck._id}
                                        deck={deck}
                                        masteryPct={deckStats.has(deck._id) ? deckStats.get(deck._id)! : null}
                                        onEdit={openEdit}
                                        onDelete={d => setDeleteTarget(d)}
                                        onGoToEditor={goToEditor}
                                        onGoToStudy={goToStudy}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}
            </div>

            {/* ── Create / Edit Modal ── */}
            {modalMode && (
                <div className="ud-overlay" onClick={closeModal}>
                    <div className="ud-dialog" onClick={e => e.stopPropagation()}>
                        <p className="ud-dialog-title">
                            {modalMode === 'create' ? 'Novo Deck' : 'Editar Deck'}
                        </p>

                        <div className="ud-field">
                            <label className="ud-label">Nome</label>
                            <input
                                className="ud-input"
                                value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Ex: Funções do 2º Grau"
                                maxLength={100}
                                autoFocus
                            />
                        </div>

                        <div className="ud-field">
                            <label className="ud-label">Descrição</label>
                            <textarea
                                className="ud-textarea"
                                value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Descrição opcional..."
                                maxLength={500}
                            />
                        </div>

                        <div className="ud-field">
                            <label className="ud-label">Cor</label>
                            <ColorPicker value={form.colorId} onChange={v => setForm(f => ({ ...f, colorId: v }))} />
                        </div>

                        <div className="ud-dialog-actions">
                            <button className="ud-btn-cancel" onClick={closeModal}>Cancelar</button>
                            <button className="ud-btn-save" onClick={handleSave} disabled={saving || !form.name.trim()}>
                                {saving ? 'Salvando...' : modalMode === 'create' ? 'Criar Deck' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Confirm Modal ── */}
            {deleteTarget && (
                <div className="ud-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="ud-dialog" onClick={e => e.stopPropagation()}>
                        <p className="ud-dialog-title">Excluir deck</p>
                        <p className="ud-delete-msg">
                            Tem certeza que deseja excluir o deck{' '}
                            <span className="ud-delete-name">"{deleteTarget.name}"</span>?
                            Todos os flashcards dentro dele serão removidos permanentemente.
                        </p>
                        <div className="ud-dialog-actions">
                            <button className="ud-btn-cancel" onClick={() => setDeleteTarget(null)}>Cancelar</button>
                            <button className="ud-btn-danger" onClick={handleDelete} disabled={saving}>
                                {saving ? 'Excluindo...' : 'Excluir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDeckPage;
